import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { IAddTeamMembersActionPayload } from 'app/library/team/models/add-team-members-action.action.payload';
import { IRemoveTeamMembersActionPayload } from 'app/library/team/models/remove-team-members-action.action.payload';
import { TeamMember } from 'app/library/team/models/team-member.model';
import { TeamsState } from 'app/library/team/models/teams.state';
import { TeamNavigationService } from 'app/library/team/services/team-navigation.service';
import { TeamService } from 'app/library/team/services/team.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { AddUserToTeamDialogComponent } from '../dialogs/add-user-to-team-dialog/add-user-to-team-dialog.component';

@Component({
    selector: 'app-team-members',
    templateUrl: './team-members.component.html',
    styleUrls: ['./team-members.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TeamMembersComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @Select(TeamsState.getTeamMembersTotalCount)
    teamMembersTotalCount$: Observable<number>;

    @Input()
    teamId: string;

    @Input()
    isTeamDetailsPage: string;

    @Input()
    canManageTeamMembers: boolean;

    @Input()
    isTrackSubTeamProgressActive: boolean;

    @Input()
    allUser$: Observable<User[]>;

    @Input()
    filteredAllUsers$: Observable<User[]>;

    @ViewChild('loadMoreSentinel')
    set loadMoreSentinel(ref: ElementRef<HTMLElement> | undefined) {
        this._intersectionObserver?.disconnect();

        if (ref?.nativeElement && this._intersectionObserver) {
            this._intersectionObserver.observe(ref.nativeElement);
        }
    }

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    addUserToTeamDialogRef: MatDialogRef<AddUserToTeamDialogComponent>;

    membersPage = 1;
    membersLimit = 6;
    isLoadingMore = false;
    shimmerPlaceholders = [1, 2, 3];

    private _intersectionObserver: IntersectionObserver = null;

    constructor(
        public sideNav: SidenavService,
        public teamService: TeamService,
        private _store: Store,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _teamNavigationService: TeamNavigationService
    ) {
        super();
    }

    ngOnInit(): void {
        this._intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    this.tryLoadMoreMembers();
                }
            },
            { root: null, rootMargin: '120px', threshold: 0 }
        );

        this.sideNav.scrolledToBottom$
            .pipe(
                debounceTime(200),
                filter((scrolledToBottom) => scrolledToBottom),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => this.tryLoadMoreMembers());
    }

    ngOnDestroy(): void {
        this._intersectionObserver?.disconnect();
        super.ngOnDestroy();
    }

    tryLoadMoreMembers(): void {
        if (this.isLoadingMore) {
            return;
        }

        const loadedCount = this._store.selectSnapshot(TeamsState.getTeamMembers)?.length || 0;
        const totalCount = this._store.selectSnapshot(TeamsState.getTeamMembersTotalCount) || 0;

        if (!totalCount || loadedCount >= totalCount) {
            return;
        }

        this.loadMoreMembers();
    }

    async loadMoreMembers(): Promise<void> {
        if (this.isLoadingMore) {
            return;
        }

        const loadedCount = this._store.selectSnapshot(TeamsState.getTeamMembers)?.length || 0;
        this.membersPage = Math.ceil(loadedCount / this.membersLimit) + 1;
        this.isLoadingMore = true;

        try {
            await this.teamService.fetchTeamMembers(this.teamId, this.membersPage, this.membersLimit, true);
        } finally {
            this.isLoadingMore = false;
        }
    }

    private async refreshTeamMembers(): Promise<void> {
        this.membersPage = 1;
        await this.teamService.fetchTeamMembers(this.teamId, this.membersPage, this.membersLimit);
    }

    // Method to open the appropriate dialog based on isTeamDetailsPage
    openAddDialog(): void {
        if (this.isTeamDetailsPage) {
            this.openAddUserToTeamDialog();
        } else {
            this.openAddMemberDialog();
        }
    }

    openAddMemberDialog(): void {
        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.allUser$;
        dialogRef.componentInstance.entityId = this.teamId;
        dialogRef.componentInstance.title = 'TEAMS.MEMBER.BUTTON.ADD_MEMBER';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'teamMember';

        dialogRef.afterClosed().subscribe(async (result: { list: User[] }) => {
            if (result && result.list) {
                const payload: IAddTeamMembersActionPayload = {
                    userIds: result.list.map((user) => user.id),
                    teamIds: [this.teamId],
                };

                await this.teamService.addUsersToTeamAsMembers(payload);
                await this.refreshTeamMembers();
                // Refetch team progress
                await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive);
            }
        });
    }

    async openAddUserToTeamDialog(): Promise<void> {
        await this.teamService.fetchTeamLeaderTeams();

        this.addUserToTeamDialogRef = this._matDialog.open(AddUserToTeamDialogComponent, {
            disableClose: false,
            width: '900px',
            autoFocus: false,
        });

        const teams = this.teamService.getTeamLeaderTeams();
        const clonedTeams = JSON.parse(JSON.stringify(teams));
        const teamListTree = this.teamService.convertListToTree(clonedTeams);

        this.addUserToTeamDialogRef.componentInstance.entities$ = this.filteredAllUsers$;
        this.addUserToTeamDialogRef.componentInstance.title = this._translateService.instant('TEAMS.MEMBER.BUTTON.ADD_MEMBER');
        this.addUserToTeamDialogRef.componentInstance.teamId = this.teamId;
        this.addUserToTeamDialogRef.componentInstance.role = 'teamMember';
        this.addUserToTeamDialogRef.componentInstance.listTree = teamListTree;

        this.addUserToTeamDialogRef.afterClosed().subscribe(async (data: { selectedNodes: Set<User> }) => {
            if (data) {
                const userIds: string[] = Array.from(data.selectedNodes).map((user) => user.id);

                const payload: IAddTeamMembersActionPayload = {
                    userIds: userIds,
                    teamIds: [this.teamId],
                };

                const result = await this.teamService.addUsersToTeamAsMembers(payload);
                await this.refreshTeamMembers();
                // Refetch team progress
                await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive);

                if (result && (result.asyncJobId || (result.teamMembers && result.teamMembers.length > 0))) {
                    const successMessage = this._translateService.instant('TEAMS.DIALOG.ADD_USER_TO_TEAM.MEMBER.MESSAGE.SUCCESS');

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
            this.addUserToTeamDialogRef = null;
        });
    }

    openRemoveTeamMemberDialog(teamMember: TeamMember): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('TEAMS.DIALOG.REMOVE_TEAM_MEMBER.MESSAGE', { fullName: teamMember.user.fullName });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const payload: IRemoveTeamMembersActionPayload = {
                    userIds: [teamMember.user.id],
                    teamId: this.teamId,
                };

                await this.teamService.removeTeamMembers(payload);
                // Refetch team progress
                await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive);
            }

            this.confirmDialogRef = null;
        });
    }

    goToTeamMember(userId: string): void {
        this._teamNavigationService.addTeamPath(window.location.pathname);
        this._store.dispatch(new Navigate([`/my-teams/team/${this.teamId}/member/${userId}`]));
    }
}
